import { useEffect, useMemo, useState } from 'react';

import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';

import { useZuubRcmOnboardingBegin } from '@/react-query/rcm/useZuubRcmOnboarding';

import type {
  ZuubOnboardingBeginResponse,
  ZuubPatientData,
} from '../../../@types/zuub/contracts';
import type { TableColumn } from '../../../common-components/composed/sortableTable/SortableTable';
import { SortableTable } from '../../../common-components/composed/sortableTable/SortableTable';
import { StatusChip } from '../../../common-components/elements/StatusChip';
import type { Appointment } from '../../../react-query/useAppointments';
import { capitalize, toBusinessName } from '../../../utils/string';
import { useLocalizedFormatDate } from '../../claims/summary/state';
import { PatientInfo } from '../../patientList/patientStrip/patientStripRedesigned/patientInfoSheet/PatientInfo';
import { PatientListPageLayoutContextMap } from '../../patientList/types';
import { ConnectToPayerPortalDialog } from '../connectCredentials/ConnectToPayerPortalDialog';
import {
  getAppointmentInsuranceStatus,
  getInsuranceStatusConfig,
  getVerificationStatusDisplay,
  sortAppointments,
} from '../helpers';

import { ActionsColumn } from './ActionsColumn';
import { EnableAdditionalBenefitsInfoHoverCard } from './EnableAdditionalBenefitsInfoHoverCard';

type SortField = 'flag' | 'patient' | 'appointment' | 'procedureCodes';
type SortDirection = 'asc' | 'desc';

interface AutoVerifyTableProps {
  appointments: Appointment[];
  zuubDataMap: Map<string, ZuubPatientData>;
  isSearchActive?: boolean;
}

export const AutoVerifyTable = ({
  appointments,
  zuubDataMap,
  isSearchActive = false,
}: AutoVerifyTableProps) => {
  // state
  const [sortField, setSortField] = useState<SortField>('appointment');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [isConnectDialogOpen, setIsConnectDialogOpen] = useState(false);
  const [selectedPayerId, setSelectedPayerId] = useState<number | null>(null);
  const [onboardingResponse, setOnboardingResponse] =
    useState<ZuubOnboardingBeginResponse | null>(null);
  const [isLoadingOnboarding, setIsLoadingOnboarding] = useState(false);
  const [onboardingError, setOnboardingError] = useState<Error | null>(null);

  // hooks
  const formatLocalizedDate = useLocalizedFormatDate();
  const beginOnboardingMutation = useZuubRcmOnboardingBegin();

  const filteredAndSortedAppointments = useMemo(
    () =>
      sortAppointments(
        appointments || [],
        sortField,
        sortDirection,
        zuubDataMap,
      ),
    [appointments, sortField, sortDirection, zuubDataMap],
  );

  const handleSort = (field: string, direction: SortDirection) => {
    setSortField(field as SortField);
    setSortDirection(direction);
  };

  // Define columns for SortableTable
  const columns: TableColumn<Appointment>[] = [
    {
      key: 'flag',
      header: '',
      width: 'vai-w-[50px]',
      isSortable: true,
      render: (appointment) => {
        const zuubPatient = zuubDataMap.get(appointment.appointment.unifiedId);
        const shouldShowFlag =
          zuubPatient?.detailedData?.some(
            (data) => data.needsPayerPortalConnection,
          ) ?? false;

        return (
          <EnableAdditionalBenefitsInfoHoverCard
            isOpen={shouldShowFlag}
            onSetUpCredentials={() => {
              const payerId = zuubPatient?.detailedData?.find(
                (data) => data.needsPayerPortalConnection,
              )?.payerId;
              if (payerId) {
                setSelectedPayerId(payerId);
                setIsLoadingOnboarding(true);
                setOnboardingError(null);
                setIsConnectDialogOpen(true);

                beginOnboardingMutation.mutate(
                  { payerId },
                  {
                    onSuccess: (response) => {
                      setOnboardingResponse(response);
                      setIsLoadingOnboarding(false);
                      setOnboardingError(null);
                    },
                    onError: (error) => {
                      setIsLoadingOnboarding(false);
                      setOnboardingError(error);
                    },
                  },
                );
              }
            }}
          />
        );
      },
    },
    {
      key: 'appointment',
      header: <Trans>Appointment</Trans>,
      width: 'vai-w-[150px]',
      isSortable: true,
      render: (appointment) => (
        <>
          {isSearchActive
            ? formatLocalizedDate(appointment.appointment.start, false, 'P')
            : formatLocalizedDate(
                appointment.appointment.start,
                false,
                'h:mmaaa',
              )}
        </>
      ),
    },
    {
      key: 'patient',
      header: <Trans>Patient</Trans>,
      width: 'vai-w-[200px]',
      isSortable: true,
      render: (appointment, index) => (
        <div className="vai-font-semibold">
          <PatientInfo
            context={PatientListPageLayoutContextMap.AutoVerify}
            appointment={appointment}
            index={index}
          />
        </div>
      ),
    },
    // Hide for now
    // Add back when codes are available from API
    // {
    //   key: 'procedureCodes',
    //   header: <Trans>Procedure Codes</Trans>,
    //   width: 'vai-w-[200px]',
    //   isSortable: true,
    //   render: (appointment) => {
    //     // TODO: Replace with actual procedure codes
    //     // Use random procedure codes for now
    //     const procedureCodes = getRandomProcedureCodes();

    //     return (
    //       <div className="vai-flex vai-gap-1 vai-flex-wrap">
    //         {procedureCodes.map((code: string) => (
    //           <div
    //             key={code}
    //             className="vai-bg-slate-200 vai-text-zinc-900 vai-rounded-full vai-w-[68px] vai-px-3 vai-py-1 vai-text-center"
    //           >
    //             {code}
    //           </div>
    //         ))}
    //       </div>
    //     );
    //   },
    // },
    {
      key: 'payerName',
      header: <Trans>Payer Name</Trans>,
      width: 'vai-w-[200px]',
      render: (appointment) => {
        const zuubPatient = zuubDataMap.get(appointment.appointment.unifiedId);
        const payerName = zuubPatient?.payerName;

        return <>{payerName ? toBusinessName(payerName) : '—'}</>;
      },
    },
    {
      key: 'verificationStatus',
      header: <Trans>Verification Status</Trans>,
      width: 'vai-w-[150px]',
      render: (appointment) => {
        const zuubPatient = zuubDataMap.get(appointment.appointment.unifiedId);
        const verificationStatus = zuubPatient?.verificationStatus
          ? getVerificationStatusDisplay(zuubPatient.verificationStatus)
          : {
              status: 'Unknown',
              icon: faQuestionCircle,
              iconColor: 'vai-text-gray-500',
            };

        return (
          <StatusChip
            variant="ghost"
            icon={<FontAwesomeIcon icon={verificationStatus.icon} />}
            text={verificationStatus.status}
            iconColor={verificationStatus.iconColor}
          />
        );
      },
    },
    {
      key: 'insuranceStatus',
      header: <Trans>Insurance Status</Trans>,
      width: 'vai-w-[150px]',
      render: (appointment) => {
        const status = getAppointmentInsuranceStatus(appointment, zuubDataMap);
        if (!status) return null;

        const { variant, icon } = getInsuranceStatusConfig(status);

        return (
          <StatusChip
            icon={icon ? <FontAwesomeIcon icon={icon} /> : undefined}
            text={capitalize(status)}
            variant={variant}
          />
        );
      },
    },
    {
      key: 'lastRun',
      header: <Trans>Last Run</Trans>,
      width: 'vai-w-[120px]',
      render: (appointment) => {
        const zuubPatient = zuubDataMap.get(appointment.appointment.unifiedId);
        const lastRun = zuubPatient?.lastRun;

        return <>{lastRun ? formatLocalizedDate(lastRun) : '—'}</>;
      },
    },
    {
      key: 'actions',
      header: <Trans>Actions</Trans>,
      width: 'vai-w-[150px]',
      render: (appointment) => {
        const zuubPatient = zuubDataMap.get(appointment.appointment.unifiedId);
        return (
          <ActionsColumn
            appointment={appointment}
            zuubPatient={zuubPatient}
            onManagePayerConnection={(payerId) => {
              setSelectedPayerId(payerId);
              setIsLoadingOnboarding(true);
              setOnboardingError(null);
              setIsConnectDialogOpen(true);

              beginOnboardingMutation.mutate(
                { payerId },
                {
                  onSuccess: (response) => {
                    setOnboardingResponse(response);
                    setIsLoadingOnboarding(false);
                    setOnboardingError(null);
                  },
                  onError: (error) => {
                    setIsLoadingOnboarding(false);
                    setOnboardingError(error);
                  },
                },
              );
            }}
          />
        );
      },
    },
  ];

  // When searching, default to descending order for appointment dates
  useEffect(() => {
    if (
      isSearchActive &&
      sortField === 'appointment' &&
      sortDirection === 'asc'
    ) {
      setSortDirection('desc');
    }
  }, [isSearchActive, sortField, sortDirection]);

  return (
    <>
      <SortableTable
        data={filteredAndSortedAppointments}
        columns={columns}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        rowKey={(appointment) =>
          `auto-verify-${appointment.appointment.unifiedId}`
        }
        className="vai-h-full"
      />
      {selectedPayerId && (
        <ConnectToPayerPortalDialog
          payerId={selectedPayerId}
          isOpen={isConnectDialogOpen}
          onIsOpenChange={(isOpen) => {
            setIsConnectDialogOpen(isOpen);
            if (!isOpen) {
              setSelectedPayerId(null);
              setOnboardingResponse(null);
              setIsLoadingOnboarding(false);
              setOnboardingError(null);
            }
          }}
          onCancel={() => {
            setIsConnectDialogOpen(false);
            setSelectedPayerId(null);
            setOnboardingResponse(null);
            setIsLoadingOnboarding(false);
            setOnboardingError(null);
          }}
          onboardingResponse={onboardingResponse}
          isLoadingOnboarding={isLoadingOnboarding}
          onboardingError={onboardingError}
        />
      )}
    </>
  );
};
